import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageMetadata from '../utils/usePageMetadata';
import './FeaturePage.css';

const ComplianceFeatures = () => {
  const { token } = useAuth();

  usePageMetadata({
    title: 'Compliance Dashboard | Ad Policy & Privacy Compliance Monitoring',
    description:
      'Stay ahead of ad policy and privacy requirements. Monitor compliance checks, fix issues proactively, and ensure your site meets all standards.',
    canonicalUrl: 'https://theadshop.co/compliance-features',
    meta: [
      { property: 'og:title', content: 'Compliance Dashboard | Ad Policy & Privacy Compliance Monitoring' },
      {
        property: 'og:description',
        content:
          'Automated compliance scanning for ads.txt, robots.txt, privacy policies, and CMP monitoring to keep your site compliant.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/compliance-features' },
      { property: 'og:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:title', content: 'Compliance Dashboard | Ad Policy & Privacy Compliance Monitoring' },
      {
        name: 'twitter:description',
        content:
          'Automated compliance checks help you stay ahead of ad policy violations and privacy requirements.',
      },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Compliance Dashboard Features | The Ad Shop',
        url: 'https://theadshop.co/compliance-features',
        description:
          'Learn about The Ad Shop Compliance Dashboard features including automated compliance scanning, ads.txt validation, privacy policy checks, and CMP monitoring.',
        isPartOf: {
          '@type': 'WebSite',
          name: 'The Ad Shop',
          url: 'https://theadshop.co/',
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://theadshop.co/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Compliance Dashboard',
              item: 'https://theadshop.co/compliance-features',
            },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'The Ad Shop Compliance Dashboard',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0.00',
          priceCurrency: 'USD',
        },
        featureList: [
          'Automated compliance scanning',
          'ads.txt validation and generation',
          'robots.txt monitoring',
          'Privacy policy compliance checks',
          'Consent Management Platform (CMP) monitoring',
          'Compliance score and detailed reporting',
        ],
        description:
          'Compliance monitoring dashboard that helps publishers stay ahead of ad policy and privacy requirements with automated scanning and detailed reporting.',
      },
    ],
  });

  return (
    <div className="feature-page">
      <div className="feature-container">
        <div className="feature-header">
          <div className="feature-icon-large">✅</div>
          <h1>Compliance Dashboard</h1>
          <p className="feature-subtitle">
            Stay ahead of ad policy and privacy requirements. Monitor compliance checks, fix issues proactively, 
            and ensure your site meets all standards.
          </p>
        </div>

        <div className="feature-content">
          <section className="feature-section">
            <h2>Automated Compliance Scanning</h2>
            <p>
              Run comprehensive compliance scans on demand to check your site against industry standards and requirements:
            </p>
            <ul className="feature-list">
              <li><strong>On-Demand Scans:</strong> Run compliance checks whenever you need them with a single click</li>
              <li><strong>Real-Time Progress:</strong> Watch scans complete in real-time with progress indicators</li>
              <li><strong>Comprehensive Coverage:</strong> Multiple compliance checks run simultaneously</li>
              <li><strong>Detailed Reporting:</strong> Get clear pass/fail/warning status for each check</li>
              <li><strong>Historical Tracking:</strong> See when reports were generated and track compliance over time</li>
            </ul>
            <p>
              No more guessing if your site is compliant. Get definitive answers in minutes, not hours.
            </p>
          </section>

          <section className="feature-section">
            <h2>ads.txt Validation & Generation</h2>
            <p>
              Ensure your ads.txt file is properly configured and compliant:
            </p>
            <ul className="feature-list">
              <li><strong>File Detection:</strong> Automatically detect if ads.txt exists and is accessible</li>
              <li><strong>Format Validation:</strong> Check that ads.txt follows the correct format and structure</li>
              <li><strong>Publisher ID Verification:</strong> Verify that your publisher IDs are correctly listed</li>
              <li><strong>Missing Entries:</strong> Identify missing authorized digital sellers</li>
              <li><strong>Generator Tool:</strong> Generate a compliant ads.txt file if one doesn't exist</li>
              <li><strong>Fix Instructions:</strong> Get step-by-step guidance on fixing any issues</li>
            </ul>
            <p>
              ads.txt violations can result in lost ad revenue. Our compliance checks help ensure you're maximizing 
              your authorized inventory.
            </p>
          </section>

          <section className="feature-section">
            <h2>robots.txt Monitoring</h2>
            <p>
              Monitor your robots.txt file to ensure proper search engine crawling:
            </p>
            <ul className="feature-list">
              <li><strong>File Accessibility:</strong> Verify robots.txt is accessible at the root domain</li>
              <li><strong>Syntax Validation:</strong> Check for proper formatting and syntax errors</li>
              <li><strong>Blocking Issues:</strong> Identify if critical pages are accidentally blocked</li>
              <li><strong>Generator Tool:</strong> Create a compliant robots.txt file if needed</li>
              <li><strong>Best Practices:</strong> Get recommendations for optimal robots.txt configuration</li>
            </ul>
            <p>
              Proper robots.txt configuration is essential for SEO. Our checks help ensure search engines can 
              crawl your site effectively.
            </p>
          </section>

          <section className="feature-section">
            <h2>Privacy Policy Compliance</h2>
            <p>
              Ensure your privacy policy meets legal and platform requirements:
            </p>
            <ul className="feature-list">
              <li><strong>Policy Detection:</strong> Verify that a privacy policy exists and is accessible</li>
              <li><strong>Required Elements:</strong> Check for required privacy policy elements and disclosures</li>
              <li><strong>GDPR Compliance:</strong> Verify GDPR-related requirements are addressed</li>
              <li><strong>CCPA Compliance:</strong> Check for California Consumer Privacy Act requirements</li>
              <li><strong>Generator Tool:</strong> Generate a compliant privacy policy template if needed</li>
              <li><strong>Update Recommendations:</strong> Get suggestions for keeping your policy current</li>
            </ul>
            <p>
              Privacy regulations are constantly evolving. Our compliance checks help ensure your privacy policy 
              stays up to date with current requirements.
            </p>
          </section>

          <section className="feature-section">
            <h2>Consent Management Platform (CMP) Monitoring</h2>
            <p>
              Track your consent management platform status and compliance:
            </p>
            <ul className="feature-list">
              <li><strong>CMP Detection:</strong> Identify if a CMP is installed and active on your site</li>
              <li><strong>Consent Signal Monitoring:</strong> Track consent signals and user choices</li>
              <li><strong>Multi-Regulation Support:</strong> Verify support for GDPR, CCPA, and other regulations</li>
              <li><strong>Integration Status:</strong> Check that CMP is properly integrated with ad partners</li>
              <li><strong>Compliance Scoring:</strong> Get a compliance score based on CMP implementation</li>
            </ul>
            <p>
              Proper consent management is critical for ad revenue and legal compliance. Our checks help ensure 
              your CMP is working correctly.
            </p>
          </section>

          <section className="feature-section">
            <h2>Compliance Score & Reporting</h2>
            <p>
              Get a clear picture of your overall compliance status:
            </p>
            <ul className="feature-list">
              <li><strong>Overall Compliance Score:</strong> Single score (0-100) representing your site's compliance status</li>
              <li><strong>Status Breakdown:</strong> See counts of passed, warning, and failed checks</li>
              <li><strong>Priority Highlights:</strong> Identify which issues need immediate attention</li>
              <li><strong>Detailed Check Cards:</strong> Expandable cards with full details for each compliance check</li>
              <li><strong>Fix Steps:</strong> Step-by-step instructions for resolving each issue</li>
              <li><strong>Report History:</strong> Track compliance improvements over time</li>
            </ul>
            <p>
              Quickly understand your compliance status at a glance, then drill down into specific issues that need attention.
            </p>
          </section>

          <section className="feature-section">
            <h2>Proactive Issue Prevention</h2>
            <p>
              Fix issues before they impact your revenue:
            </p>
            <ul className="feature-list">
              <li><strong>Early Detection:</strong> Catch compliance issues before they become problems</li>
              <li><strong>Revenue Protection:</strong> Prevent ad revenue loss from policy violations</li>
              <li><strong>Legal Risk Reduction:</strong> Stay ahead of privacy regulation requirements</li>
              <li><strong>Automated Alerts:</strong> Get notified when compliance status changes</li>
              <li><strong>Best Practices:</strong> Learn industry best practices for each compliance area</li>
            </ul>
            <p>
              Don't wait for ad networks or regulators to flag issues. Our proactive compliance monitoring helps 
              you stay ahead of problems.
            </p>
          </section>

          <section className="feature-section">
            <h2>Multi-Site Management</h2>
            <p>
              Manage compliance across all your properties:
            </p>
            <ul className="feature-list">
              <li><strong>Site Selector:</strong> Switch between sites to check compliance for each property</li>
              <li><strong>Individual Reports:</strong> Separate compliance reports for each site</li>
              <li><strong>Bulk Scanning:</strong> Run scans across multiple sites efficiently</li>
              <li><strong>Centralized Dashboard:</strong> View compliance status for all sites in one place</li>
            </ul>
            <p>
              Perfect for publishers managing multiple properties. Keep all your sites compliant from one dashboard.
            </p>
          </section>

          <section className="feature-cta">
            <h2>Ready to Stay Compliant?</h2>
            <p>
              Start monitoring compliance and fix issues before they impact your revenue.
            </p>
            {token ? (
              <Link to="/compliance" className="btn-primary">
                Go to Compliance Dashboard
              </Link>
            ) : (
              <Link to="/signup" className="btn-primary">
                Sign Up Free
              </Link>
            )}
            <p className="cta-note">Free to get started • No credit card required • Setup in minutes</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ComplianceFeatures;

